import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Alert,
  Button,
  Container,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
  apiErrorMessage,
  appointmentsService,
  inventoryService,
  reportsService,
} from '../../services/api';
import { BlockSkeleton, CardSkeleton, PageHeader, StatCard } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useShop } from '../../contexts/ShopContext';
import { useSocket } from '../../contexts/SocketContext';
import { useShopTimezones } from '../../hooks/useShopTimezones';
import { useCachedData } from '../../hooks/useCachedData';
import {
  Appointment,
  AppointmentStatus,
  DashboardSummary,
  Part,
  WorkshopFloor as Floor,
} from '../../types';
import WorkshopFloor from './dashboard/WorkshopFloor';
import AssignBayDialog from './dashboard/AssignBayDialog';
import WeekRevenue from './dashboard/WeekRevenue';
import TodayAppointments from './dashboard/TodayAppointments';
import LowStockParts from './dashboard/LowStockParts';
import ReportCharts from './dashboard/ReportCharts';
import { bayCandidates, greetingFor, shopCalendar } from './dashboard/helpers';

/**
 * Shop dashboard: what is happening on the floor now (bays, today's bookings,
 * this week's takings, parts to reorder), then the period reports below.
 *
 * The live half reloads whenever the socket reports an appointment change, so
 * a job moved on from another screen shows up here without a refresh. The
 * period half follows the range picker.
 */

const DAY = 86_400;

const RANGES = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
] as const;

type RangeKey = (typeof RANGES)[number]['key'];

interface LiveState {
  floor: Floor | null;
  today: Appointment[];
  week: DashboardSummary | null;
  lowParts: Part[];
  lowPartsTotal: number;
}

const EMPTY_LIVE: LiveState = { floor: null, today: [], week: null, lowParts: [], lowPartsTotal: 0 };

const DashboardPage: React.FC = () => {
  const { selectedShop } = useShop();
  const { user } = useAuth();
  const { appointments: socketAppointments, connected } = useSocket();
  const tzOf = useShopTimezones([selectedShop?.id]);
  const timeZone = tzOf(selectedShop?.id);
  const shopId = selectedShop?.id;

  const [rangeKey, setRangeKey] = useState<RangeKey>('30d');
  const [error, setError] = useState<string | null>(null);

  const [assignBay, setAssignBay] = useState<number | null>(null);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Read on the shop's clock, so "today" is the shop's today for any viewer.
  const calendar = useMemo(() => shopCalendar(timeZone), [timeZone]);

  // Both halves come from the session cache first, so returning to the
  // dashboard paints at once while it reloads.
  const reports = useCachedData(`dashboard:reports:${shopId}:${rangeKey}`, () => {
    const days = RANGES.find((range) => range.key === rangeKey)?.days ?? 30;
    const to = Math.floor(Date.now() / 1000);
    return reportsService.getSummary({ from: to - days * DAY, to }, shopId);
  });
  // Another range keeps the previous numbers on screen until its own arrive.
  const lastSummary = useRef<{ shopId?: string; summary: DashboardSummary } | null>(null);
  if (reports.data) lastSummary.current = { shopId, summary: reports.data };
  const previous = lastSummary.current;
  const summary = reports.data ?? (previous && previous.shopId === shopId ? previous.summary : null);
  const reportsLoading = reports.loading;

  const liveData = useCachedData<LiveState>(
    shopId ? `dashboard:live:${shopId}:${timeZone ?? ''}` : null,
    async () => {
      const { day, week } = shopCalendar(timeZone);
      const [floor, today, weekSummary, parts] = await Promise.all([
        appointmentsService.getWorkshopFloor(shopId!),
        appointmentsService.getShopAppointments(shopId!, { from: day.from, to: day.to }),
        reportsService.getSummary({ from: week.from, to: week.to }, shopId),
        inventoryService.listParts({ lowStock: true, activeOnly: true, limit: 50, shopId }),
      ]);
      return { floor, today, week: weekSummary, lowParts: parts.rows, lowPartsTotal: parts.total };
    },
  );
  const live = liveData.data ?? EMPTY_LIVE;
  const liveLoading = liveData.loading;
  const loadLive = liveData.reload;

  // socketAppointments changes on every appointment event the gateway sends.
  // The first value is the one the cached load above already covers.
  const seenSocketEvent = useRef(socketAppointments);
  useEffect(() => {
    if (seenSocketEvent.current === socketAppointments) return;
    seenSocketEvent.current = socketAppointments;
    void loadLive();
  }, [loadLive, socketAppointments]);

  const loadError = reports.error
    ? apiErrorMessage(reports.error, 'Could not load the dashboard.')
    : liveData.error
      ? apiErrorMessage(liveData.error, 'Could not load the workshop floor.')
      : null;

  const handleAssign = async (job: Appointment, bay: number) => {
    setAssignSaving(true);
    setAssignError(null);
    try {
      await appointmentsService.updateAppointment(job.id, {
        bay,
        // A car going into a bay is a car being worked on.
        ...(job.status === AppointmentStatus.IN_PROGRESS ? {} : { status: AppointmentStatus.IN_PROGRESS }),
      });
      setAssignBay(null);
      await loadLive();
    } catch (err) {
      setAssignError(apiErrorMessage(err, 'Could not put the job in that bay.'));
    } finally {
      setAssignSaving(false);
    }
  };

  const handleRelease = async (job: Appointment) => {
    try {
      await appointmentsService.updateAppointment(job.id, { bay: null });
      await loadLive();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not take the job out of its bay.'));
    }
  };

  const floorJobs = live.floor?.jobs ?? [];
  const bayCount = live.floor?.bayCount ?? 0;
  const inProgress = floorJobs.filter((job) => job.status === AppointmentStatus.IN_PROGRESS).length;
  const doneInBay = floorJobs.length - inProgress;
  const utilisation = bayCount ? Math.round((Math.min(floorJobs.length, bayCount) / bayCount) * 100) : 0;
  const readyForPickup = summary?.appointments.byStatus[AppointmentStatus.READY_FOR_PICKUP] ?? 0;

  const firstName = user?.name?.trim().split(/\s+/)[0];
  const title = firstName ? `${greetingFor(calendar.now.getHours())}, ${firstName}` : 'Dashboard';
  const subtitle = [
    bayCount ? `${floorJobs.length} of ${bayCount} bays are busy.` : null,
    readyForPickup
      ? `${readyForPickup} ${readyForPickup === 1 ? 'vehicle is' : 'vehicles are'} ready for pickup.`
      : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        eyebrow={format(calendar.now, 'EEEE, MMMM d')}
        title={title}
        subtitle={subtitle || undefined}
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={rangeKey}
              onChange={(_event, value: RangeKey | null) => value && setRangeKey(value)}
              aria-label="Reporting period"
            >
              {RANGES.map((range) => (
                <ToggleButton key={range.key} value={range.key}>
                  {range.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {/* Staff book from the time slots, inside the admin screens */}
            <Button
              component={RouterLink}
              to="/admin/appointments?view=slots"
              variant="contained"
              startIcon={<AddIcon />}
            >
              New booking
            </Button>
          </Stack>
        }
      />

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Headline numbers: money for the selected period, the floor right now */}
        {reportsLoading && !summary ? (
          <CardSkeleton count={4} height={120} />
        ) : summary ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                label="Collected"
                value={summary.revenue.collected}
                prefix="$"
                tone="success"
                icon={<PaidOutlinedIcon />}
                caption="Received in this period"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                label="Jobs in progress"
                value={inProgress}
                tone="info"
                icon={<BuildOutlinedIcon />}
                caption={doneInBay ? `${doneInBay} done, waiting on an invoice` : 'In the bays right now'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                label="Bay utilization"
                value={utilisation}
                suffix="%"
                tone="neutral"
                icon={<SpeedOutlinedIcon />}
                caption={bayCount ? `${floorJobs.length} of ${bayCount} bays busy` : 'Set up bays in settings'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                label="Average ticket"
                value={summary.revenue.averageTicket}
                prefix="$"
                tone="info"
                icon={<EventAvailableOutlinedIcon />}
                caption={`${summary.appointments.total} appointments`}
              />
            </Grid>
          </Grid>
        ) : null}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <WorkshopFloor
              floor={live.floor}
              loading={liveLoading}
              live={connected}
              onAssign={(bay) => {
                setAssignError(null);
                setAssignBay(bay);
              }}
              onRelease={handleRelease}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <WeekRevenue summary={live.week} todayKey={calendar.todayKey} loading={liveLoading} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <TodayAppointments appointments={live.today} loading={liveLoading} timeZone={timeZone} />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <LowStockParts parts={live.lowParts} total={live.lowPartsTotal} loading={liveLoading} />
          </Grid>
        </Grid>

        {summary ? (
          <ReportCharts summary={summary} />
        ) : reportsLoading ? (
          <BlockSkeleton lines={8} />
        ) : null}
      </Stack>

      <AssignBayDialog
        bay={assignBay}
        candidates={bayCandidates(live.today, floorJobs)}
        timeZone={timeZone}
        saving={assignSaving}
        error={assignError}
        onClose={() => setAssignBay(null)}
        onAssign={handleAssign}
      />
    </Container>
  );
};

export default DashboardPage;
